import * as React from "react";
import { DebounceInput as DBInput } from "react-debounce-input";
import { Field as FormikField } from "formik";
import { lighten } from "polished";
import styled, { css } from "styled-components";

import {
  media,
  sentenceCase
} from "src/library/utils";

import Grid from "../Hedron";
import { LoadingSpinner } from "../LoadingSpinner";
import {
  IFormInput,
  FormFieldvalue
} from ".";
import Label from "./Label";
import FieldError from "./FieldError";

export const InputCss = css`
  ${({ theme }) => css`
    font-family: ${theme.fonts.family.body || "Roboto"}, Helvetica;
    transition: ${theme.transition.quicker};
    padding: 10px 15px;
    width: 100%;
    outline: none;
    max-width: 400px;
    appearance: none;

    &::-webkit-input-placeholder {
      color: ${theme.fonts.colors.secondary};
    }

    &::-moz-placeholder {
      color: ${theme.fonts.colors.secondary};
    }

    &:-ms-input-placeholder {
      color: ${theme.fonts.colors.secondary};
    }

    &:-moz-placeholder {
      color: ${theme.fonts.colors.secondary};
    }

    &:-webkit-autofill {
      background-color: inherit;
      -webkit-text-fill-color: inherit;
      transition-delay: 99999s;
    }

    transition: ${theme.transition.quicker};
    color: ${theme.fonts.colors.primary};
    font-size: ${theme.fonts.sizes.p};
    line-height: 1.2em;
    background: ${theme.colors.white};
    border: 2px solid ${theme.colors.lightGrey};
    border-radius: ${theme.borderRadius};
    outline: none;
    height: 40px;

    &:disabled {
      color: ${lighten(0.3, theme.colors.midGrey)};

      &:hover {
        cursor: not-allowed;
      }
    }

    ${media({ lt: theme.breakpoints.sm })} {
      /* This fixes zoom issue on ios */
      font-size: 16px;
    }
  `}
`;

export const InputStyled = styled(FormikField)`
  max-width: 400px;
  ${InputCss};
  appearance: none;
`;

InputStyled.defaultProps = { type: "text" };

const FormItemWrapper = styled(Grid.Box)``;

const InputContainer = styled(Grid.Box)`
  ${({ theme }) => css`
    display: flex;

    &:hover *:not(.input-wrapper) {
      border-color: ${({ theme }) => theme.colors.midGrey};
      background-color: ${({ theme }) => theme.colors.white};
    }

    &:focus *:not(.input-wrapper) {
      border-color: ${({ theme }) => theme.colors.primaryLight};
      background-color: ${({ theme }) => theme.colors.white};
    }

    &.error *:not(.input-wrapper) {
      border-color: ${({ theme }) => theme.colors.error};

      &:hover {
        border-color: ${({ theme }) => theme.colors.error};
        background-color: ${({ theme }) => theme.colors.errorLight};
      }

      &:focus {
        border-color: ${({ theme }) => theme.colors.error};
        background-color: ${({ theme }) => theme.colors.errorLight};
      }
    }

    &.prependActive ${InputStyled} {
      border-radius: 0px 4px 4px 0px;
    }
    &.appendActive ${InputStyled} {
      border-radius: 4px 0px 0px 4px;
    }
    &.appendActive.prependActive ${InputStyled} {
      border-radius: 0px;
    }

    .input-styled,
    .prepend-item,
    .append-item {
      transition: ${({ theme }) => theme.transition.quicker};
      color: ${({ theme }) => theme.fonts.colors.primary};
      font-size: ${({ theme }) => theme.fonts.sizes.p};
      line-height: 1.2em;
      background: ${({ theme }) => theme.colors.white};
      border: 2px solid ${({ theme }) => theme.colors.lightGrey};
      border-radius: ${({ theme }) => theme.borderRadius};
      outline: none;
      height: 40px;

      &:disabled {
        color: ${({ theme }) => lighten(0.3, theme.colors.midGrey)};

        &:hover {
          cursor: not-allowed;
        }
      }

      ${media({ lt: theme.breakpoints.sm })} {
        /* This fixes zoom issue on ios */
        font-size: 16px;
      }
    }
  `}
`;

const InputWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  position: relative;
  border: 0;
  border-radius: none;
  height: auto;

  .loading-spinner {
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
    border: 0;
    border-radius: none;
    height: auto;
  }
`;

const PrependBox = styled.div`
  border: 2px solid #eaedf0;
  border-radius: 4px 0 0 4px !important;
  border-right: 0px !important;
  outline: none;
  height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AppendBox = styled.div`
  border: 2px solid #eaedf0;
  border-radius: 0px 4px 4px 0px !important;
  border-left: 0px !important;
  outline: none;
  height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = React.forwardRef<unknown, IFormInput>(
  (
    {
      Append,
      Prepend,
      as,
      label,
      name,
      inputType,
      placeholder,
      handleBlur,
      handleChange,
      handleKeyUp,
      error,
      value,
      disabled,
      autoHideLabels,
      defaultValue,
      loading,
      autoComplete,
      forceHideLabel,
      step,
      validation,
      ...rest
    },
    ref
  ) => {
    return (
      <FormItemWrapper className="form-item">
        {showLabel(forceHideLabel, autoHideLabels, label, placeholder, value) ? (
          <Label
            for={name}
            label={label}
            required={validation?._exclusive?.required}
          />
        ) : null}

        <InputContainer
          className={`input-container ${disabled ? " disabled " : ""}
        ${Prepend ? " prependActive " : " "}
        ${Append ? " appendActive " : " "}
        ${error && error.length > 0 && " error "}`}
        >
          {Prepend && (
            <PrependBox className="prepend-item">
              {Prepend}
            </PrependBox>
          )}

          <InputWrapper className="input-wrapper">
            <InputStyled
              ref={ref}
              className="input-styled"
              disabled={disabled}
              id={name}
              name={name}
              step={step}
              type={inputType || "text"}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyUp={handleKeyUp}
              placeholder={placeholder}
              value={value}
              // @ts-ignore
              as={as}
              defaultValue={defaultValue}
              autoComplete={autoComplete}
              {...rest}
            />

            {loading && (
              <LoadingSpinner
                className="loading-spinner"
                type="circular"
                position="inline"
                size={20}
              />
            )}
          </InputWrapper>

          {Append && (
            <AppendBox className="append-item">
              {Append}
            </AppendBox>
          )}
        </InputContainer>

        {error && (
          <FieldError>
            {sentenceCase(error)}
          </FieldError>
        )}
      </FormItemWrapper>
    );
  }
);

/**
 * Decideds whether a input label should be show
 *
 * @params autoHideLabels
 * @params label
 * @params placeholder
 * @params value
 */
const showLabel = (
  forceHideLabel = false,
  autoHideLabels = false,
  label: string | undefined,
  placeholder: string | undefined,
  value: FormFieldvalue | undefined
) => {
  if (forceHideLabel) {
    return false;
  }

  if (!label || label.length === 0) {
    return false;
  }

  if ((label && label.length > 0 && placeholder && placeholder.length > 0 && autoHideLabels && value) || !autoHideLabels) {
    return true;
  }

  if (autoHideLabels) {
    return false;
  }

  return false;
};

export const DebounceInput = styled(DBInput)`
  ${InputCss};
`;

export default Input;
